import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrDataGridPro from '../../../common/arr-data-grid-pro/ArrDataGridPro';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import NoJobs from './NoJobs';
import {
  GridAlignment,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import FilterTextField from '../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import './JobList.scss';
import {columnsData, docTypesMapping, IColumnsData, tableProps} from './utils';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {ArrMenuHandle} from '../../../common/arr-menu/ArrMenu';
import ArrModal, {ModalButton} from '../../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as MoreIcon} from '../../../../assets/images/icon-more.svg';
import {ReactComponent as NoDataIcon} from '../../../../assets/images/no-data-found.svg';
import {
  artifactsStatus,
  docType,
  IAIReviewJobGroupsResponse,
  IGetDocListMasterIdJobDocsPayload,
  IGetAIReviewJobDetailsResponse,
  IGetJobListResponse,
} from '../../../../api/types/aiReview.type';
import {IAIReviewJobsExportRequest} from '../../../../api/types/aiReview.type';
import {getProtocolVersionDetails} from '../../../../api/endpoints/aiReviewApi';
import {
  useAddJobToGroup,
  useDeleteAIReviewJob,
  useRemoveJobFromGroup,
  useRetryAIReviewJob,
  useGetJobList,
  useGetAIReviewJobDetailData,
  useGetAIReviewJobResultForExport,
  useGetDocListMasterIdJobDocs,
} from '../../../../hooks/ai/review/useReview';
import {
  BgNavigationTypes,
  SourceTypeCode,
  ToastType,
} from '../../../../shared/constants';
import JobListActionMenuItem from './JobListActionMenuItem';
import {
  AIReviewJobStatus,
  getJobListMenuItems,
  parseJobListRowStatus,
  exportAIReviewJobDetailsPdf,
} from './utils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {IJobGroup} from '../utils';
import CellWrapper from '../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {InfoOutline} from '@mui/icons-material';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import ArrDocMaxLimitWarning from '../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  AI_REVIEW_EVALUATION_TYPE,
  AI_REVIEW_JOB_TYPE,
} from '../constant/AiReviewConstants';
import {
  isProtocolVersionDetails,
  type ProtocolPdfContent,
} from '../protocol-list/protocol-pdf/protocolPdf';

interface IJobList {
  reviewJobGroupsResponse?: IAIReviewJobGroupsResponse;
  onExportPayloadChange?: (payload: IAIReviewJobsExportRequest) => void;
  onHasJobsChange?: (hasJobs: boolean) => void;
  onAllJobsCountChange?: (count: number) => void;
  setIsCreateJob: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGroup: IJobGroup | null;
}

const getJobProtocolContent = (
  jobData: IGetAIReviewJobDetailsResponse
): ProtocolPdfContent => ({
  caseContext: jobData.structuredCaseContext ?? jobData.caseContext,
  relevanceCriteria:
    jobData.structuredRelevanceCriteria ?? jobData.relevanceCriteria,
  issueCoding: jobData.structuredIssueCoding ?? jobData.issueCodes,
});

/** Set when opening the kebab menu; cleared on menu close. Fixes menu items using stale React state. */
type MenuAnchorRow = {
  status: AIReviewJobStatus | null;
  groupName: string | null;
  jobName: string | null;
  jobId: string | number;
};

type JobActionToastState = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

const defaultJobActionToastState: JobActionToastState = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

const MANAGE_AI_REVIEW_JOBS_DATA_UPDATED = 'ManageAIReviewJobsDataUpdated';

const normalizeSignalRJobStatus = (
  rawStatus: string | null | undefined
): AIReviewJobStatus | null => {
  if (!rawStatus) return null;
  return parseJobListRowStatus(rawStatus);
};

const getSignalRExecutionDate = (
  payload: {
    executionDate?: string | null;
    executedDate?: string | null;
    executedAt?: string | null;
  } | null
): string | null | undefined =>
  payload?.executionDate ?? payload?.executedDate ?? payload?.executedAt;

const artifactsStatusAndTooltip = {
  [artifactsStatus.custom]: t('ai.review.jobList.protocolInfo.clone'),
  [artifactsStatus.modify]: t('ai.review.jobList.protocolInfo.modify'),
};

const EMPTY_CELL_VALUE = '---';

type JobListResponseWithLegacyTotal = IGetJobListResponse & {
  totalRecords?: number;
};

const getServerJobCount = (
  response: JobListResponseWithLegacyTotal | null | undefined
): number | undefined => {
  const total = response?.totalCount ?? response?.totalRecords;
  return typeof total === 'number' ? total : undefined;
};

const isBlankCellValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim().length === 0);

const normalizeCellValue = (value: unknown): string | number =>
  isBlankCellValue(value) ? EMPTY_CELL_VALUE : (value as string | number);

const formatCellValue = (
  value: unknown,
  rowStatus: unknown,
  col: IColumnsData
): string | number =>
  normalizeCellValue(col.formatFunction?.(value, rowStatus) ?? value);

const cellText = (
  text: unknown,
  className: string,
  dataTestId: string,
  cellParams: any,
  col: {field: string}
): React.ReactElement => {
  const displayText = normalizeCellValue(text);
  const rowData = cellParams.row;

  let protocolType = '';

  if (col?.field === 'ProtocolVersion') {
    if (rowData?.isCustom) protocolType = artifactsStatus.custom;
    else if (rowData?.isProtocolModified) protocolType = artifactsStatus.modify;
    else if (rowData?.isManual) protocolType = artifactsStatus.manual;
    else if (rowData?.isSaved) protocolType = artifactsStatus.saved;
  }

  return (
    <Box className="cell-items" style={{display: 'flex', alignItems: 'center'}}>
      <CellWrapper
        toolTipContent={String(displayText)}
        dataTestId={`tooltip-cell-content-${cellParams.id} `}
        cellParams={cellParams}
      >
        <Typography
          className={`cell-content typo-text body3_size ${className}`}
          data-testid={dataTestId ?? `job-list-typography`}
        >
          {displayText}
        </Typography>
      </CellWrapper>
      {col?.field == 'ProtocolVersion' &&
        Object.keys(artifactsStatusAndTooltip).includes(protocolType) && (
          <ArrTooltipWrapper
            trigger={
              <InfoOutline
                fontSize="small"
                role="button"
                tabIndex={0}
                aria-hidden={false}
                aria-label={t('ai.review.jobList.protocolInfo.accessibleLabel')}
              />
            }
            title={'protocol version tooltip'}
            dataTestId="protocol-version-tooltip"
            tooltipClassName="protocol-version-list"
          >
            {
              artifactsStatusAndTooltip[
                protocolType as keyof typeof artifactsStatusAndTooltip
              ]
            }
          </ArrTooltipWrapper>
        )}
    </Box>
  );
};

const JobList = (props: IJobList) => {
  const {onExportPayloadChange, onHasJobsChange, onAllJobsCountChange} = props;
  const selectedGroupId = props.selectedGroup?.jobGroupId ?? null;
  const jobGroupNamesSignature = useMemo(
    () =>
      (props.reviewJobGroupsResponse?.jobGroups ?? [])
        .map((group) => `${group.jobGroupId}:${group.name}`)
        .join('|'),
    [props.reviewJobGroupsResponse]
  );
  const navigate = useNavigate();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = useState<GridSortModel | null>(null);

  const [pageSize, setPageSize] = useState<number>(
    tableProps.DEFAULT_PAGE_SIZE
  );
  const [pageNumber, setPageNumber] = useState<number>(0);
  const previousSelectedGroupIdRef = useRef<number | null | undefined>(
    undefined
  );
  const previousJobGroupNamesSignatureRef = useRef<string | null>(null);

  const menuItemsRef = useRef<ArrMenuHandle | null>(null);
  const menuAnchorRowRef = useRef<MenuAnchorRow | null>(null);

  const [selectedRowStatus, setSelectedRowStatus] =
    useState<AIReviewJobStatus | null>(null);
  const [selectedRowJobName, setSelectedRowJobName] = useState<string | null>(
    null
  );
  const [selectedRowGroupName, setSelectedRowGroupName] = useState<
    string | null
  >(null);
  const [selectedRowJobId, setSelectedRowJobId] = useState<string | null>(null);
  const [selectedMenuGroupName, setSelectedMenuGroupName] = useState<
    string | null
  >(null);
  const [isAddToGroupExpanded, setIsAddToGroupExpanded] =
    useState<boolean>(false);
  const [groupSearchValue, setGroupSearchValue] = useState<string>('');
  const [appliedGroupSearch, setAppliedGroupSearch] = useState<string>('');
  const [groupSearchPerformed, setGroupSearchPerformed] =
    useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteJobId, setPendingDeleteJobId] = useState<
    string | number | null
  >(null);
  const [pendingDeleteJobName, setPendingDeleteJobName] = useState<string>('');
  const [jobActionToast, setJobActionToast] = useState<JobActionToastState>(
    defaultJobActionToastState
  );
  const [hasJobListLoadError, setHasJobListLoadError] = useState(false);
  const [liveJobsData, setLiveJobsData] = useState<IGetJobListResponse | null>(
    null
  );
  const lastRefetchedSignalREventRef = useRef<unknown>(null);
  const [isNavigatingToDocList, setIsNavigatingToDocList] =
    useState<boolean>(false);
  const [goToDocListPayload, setGoToDocListPayload] =
    useState<IGetDocListMasterIdJobDocsPayload>({
      reviewJobId: null,
      type: 'all',
    });
  const [showDocLimitWarning, setShowDocLimitWarning] = useState(false);
  const goToDocListPayloadDetails = useRef<
    {
      totalDocsToView: number;
    } & IGetDocListMasterIdJobDocsPayload
  >({
    ...goToDocListPayload,
    totalDocsToView: 0,
  });

  const {mutateAsync: getAIReviewJobDetailData} = useGetAIReviewJobDetailData();
  const {mutateAsync: getAIReviewJobResultForExport} =
    useGetAIReviewJobResultForExport();
  const {mutateAsync: deleteJob} = useDeleteAIReviewJob();
  const {mutateAsync: retryJob} = useRetryAIReviewJob();
  const {mutateAsync: addJobToGroup} = useAddJobToGroup();
  const {mutateAsync: removeJobFromGroup} = useRemoveJobFromGroup();

  const {data: docListData, isError: isGetMasterIdError} =
    useGetDocListMasterIdJobDocs(goToDocListPayload);

  const lastEvent = useSelector(
    (state: RootState) => state.signalr?.lastEvent ?? null
  );
  const dispatch = useDispatch();

  const openJobActionToast = useCallback(
    (message: string, status: ToastType) => {
      setJobActionToast({
        isOpen: true,
        message,
        status,
      });
    },
    []
  );

  const {
    mutate: getJobList,
    data: jobsData,
    isPending: isLoadingJobs,
  } = useGetJobList(
    (response) => {
      const hasValidResponse = !!response && Array.isArray(response.jobs);
      setHasJobListLoadError(!hasValidResponse);
    },
    () => {
      setHasJobListLoadError(true);
    }
  );

  const availableGroups = useMemo(
    () =>
      (props.reviewJobGroupsResponse?.jobGroups ?? []).map((group) => ({
        id: String(group.jobGroupId),
        group_name: group.name,
      })),
    [props.reviewJobGroupsResponse]
  );

  const handleActionsMenuClose = useCallback(() => {
    menuAnchorRowRef.current = null;
  }, []);

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    rowStatus: AIReviewJobStatus | null,
    rowGroupName: string | null,
    rowJobName: string | null,
    rowJobId: string
  ) => {
    menuAnchorRowRef.current = {
      status: rowStatus,
      groupName: rowGroupName,
      jobName: rowJobName,
      jobId: rowJobId,
    };
    setSelectedRowStatus(rowStatus);
    setSelectedRowJobName(rowJobName);
    setSelectedRowGroupName(rowGroupName);
    setSelectedRowJobId(rowJobId);
    setSelectedMenuGroupName(null);
    setIsAddToGroupExpanded(false);
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
    menuItemsRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPendingDeleteJobId(null);
    setPendingDeleteJobName('');
  };

  const handleConfirmDeleteJob = async () => {
    const jobId = pendingDeleteJobId;
    const jobName = pendingDeleteJobName;
    if (!jobId) {
      handleCloseDeleteModal();
      return;
    }
    try {
      await deleteJob({jobId});
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.deleteJobSuccess', {
          jobName: jobName ?? '',
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.deleteJobFailed', {
          jobName: jobName ?? '',
        }),
        ToastType.Error
      );
    }
    setIsDeleteModalOpen(false);
    setPendingDeleteJobId(null);
    setPendingDeleteJobName('');
  };

  const handleExportJobDetailsAsPdf = async (jobId: string | number | null) => {
    if (!jobId) return;

    try {
      const exportedAt = new Date().toISOString();
      const getJsonData = await getAIReviewJobDetailData({jobId});
      const normalizedStatus = parseJobListRowStatus(getJsonData.status);
      const normalizedJobType = String(getJsonData.jobType ?? '').toUpperCase();
      const usesJobProtocolSnapshot =
        getJsonData.isManual || getJsonData.isProtocolModified;
      let protocolContent: ProtocolPdfContent;

      if (usesJobProtocolSnapshot) {
        protocolContent = getJobProtocolContent(getJsonData);
      } else {
        const versionId = Number(getJsonData.reviewProtocolVersionID);
        if (!Number.isInteger(versionId) || versionId <= 0) {
          throw new Error('A saved protocol version is required for export');
        }

        const protocolDetails = await getProtocolVersionDetails({versionId});
        if (!isProtocolVersionDetails(protocolDetails)) {
          throw new Error('Invalid protocol version payload');
        }
        protocolContent = protocolDetails;
      }

      const jobResult =
        normalizedStatus === AIReviewJobStatus.Complete
          ? await getAIReviewJobResultForExport({
              jobId,
              evaluationType:
                normalizedJobType === AI_REVIEW_JOB_TYPE.relevancy
                  ? AI_REVIEW_EVALUATION_TYPE.relevancy
                  : AI_REVIEW_EVALUATION_TYPE.issueCode,
            })
          : undefined;
      exportAIReviewJobDetailsPdf(
        getJsonData,
        jobResult,
        exportedAt,
        protocolContent
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.noRecordFoundForJobId'),
        ToastType.Error
      );
    }
  };

  const handleRetryJob = async (
    jobId: string | number | null,
    jobName: string | null
  ) => {
    if (!jobId) return;
    try {
      await retryJob({jobId});
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.retrySuccess', {
          jobName: jobName ?? '',
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.retryFailed', {
          jobName: jobName ?? '',
        }),
        ToastType.Error
      );
    }
  };

  const handleAddJobToGroup = async (groupId: string, groupName: string) => {
    const jobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;
    const jobName = menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
    if (!jobId) return;
    const currentAssignedGroupName =
      menuAnchorRowRef.current?.groupName ??
      selectedMenuGroupName ??
      selectedRowGroupName;

    if (currentAssignedGroupName === groupName) {
      setSelectedMenuGroupName(groupName);
      setSelectedRowGroupName(groupName);
      setIsAddToGroupExpanded(false);
      return;
    }

    try {
      if (currentAssignedGroupName) {
        const currentGroup = availableGroups.find(
          (group) => group.group_name === currentAssignedGroupName
        );
        if (currentGroup) {
          await removeJobFromGroup({
            groupId: currentGroup.id,
            jobId: String(jobId),
          });
        }
      }
      await addJobToGroup({
        groupId,
        jobId: String(jobId),
      });
      setSelectedMenuGroupName(groupName);
      setSelectedRowGroupName(groupName);
      setIsAddToGroupExpanded(false);
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.addToGroupSuccess', {
          jobName: jobName ?? '',
          groupName,
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.addToGroupFailed', {
          jobName: jobName ?? '',
          groupName,
        }),
        ToastType.Error
      );
    }
  };

  const handleRemoveJobFromGroup = async (groupName: string | null) => {
    const jobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;
    const jobName = menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
    const groupToRemove = groupName ?? selectedRowGroupName;
    if (!jobId || !groupToRemove) return;
    const targetGroup = availableGroups.find(
      (group) => group.group_name === groupToRemove
    );
    if (!targetGroup) return;
    try {
      await removeJobFromGroup({
        groupId: targetGroup.id,
        jobId: String(jobId),
      });
      setSelectedMenuGroupName(null);
      setSelectedRowGroupName(null);
      fetchJobList();
      openJobActionToast(
        t('ai.review.jobList.jobListActions.removeFromGroupSuccess', {
          jobName: jobName ?? '',
          groupName: groupToRemove,
        }),
        ToastType.Success
      );
    } catch {
      openJobActionToast(
        t('ai.review.jobList.jobListActions.removeFromGroupFailed', {
          jobName: jobName ?? '',
          groupName: groupToRemove,
        }),
        ToastType.Error
      );
    }
  };

  const visibleGroups = useMemo(() => {
    const query = appliedGroupSearch.trim().toLowerCase();
    if (!query) return availableGroups;
    return availableGroups.filter((g) =>
      g.group_name.toLowerCase().includes(query)
    );
  }, [appliedGroupSearch, availableGroups]);

  const handleGroupSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupSearchValue(e.target.value);
  };

  const handleGroupSearchKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      const trimmed = groupSearchValue.trim();
      setAppliedGroupSearch(trimmed);
      setGroupSearchPerformed(trimmed.length > 0);
    }
  };

  const handleGroupSearchClear = () => {
    setGroupSearchValue('');
    setAppliedGroupSearch('');
    setGroupSearchPerformed(false);
  };

  useEffect(() => {
    if (!isAddToGroupExpanded) {
      handleGroupSearchClear();
    }
  }, [isAddToGroupExpanded]);

  const effectiveRowStatus =
    menuAnchorRowRef.current?.status ?? selectedRowStatus;
  const effectiveGroupName =
    menuAnchorRowRef.current?.groupName ?? selectedRowGroupName;
  const effectiveJobName =
    menuAnchorRowRef.current?.jobName ?? selectedRowJobName;
  const effectiveJobId = menuAnchorRowRef.current?.jobId ?? selectedRowJobId;

  const menuItems = getJobListMenuItems(effectiveRowStatus);
  const hasActionsMenu = menuItems.length > 0;

  const exportPayload = useMemo<IAIReviewJobsExportRequest>(() => {
    const columnFilter = [
      ...filterModel.items
        .filter((item) => item?.field && item.value != null)
        .map((item) => ({
          columnName: String(item.field),
          columnValue: String(item.value).trim(),
        }))
        .filter((item) => item.columnValue.length > 0),
      ...(selectedGroupId !== null
        ? [
            {
              columnName: 'JobGroupId',
              columnValue: selectedGroupId.toString(),
            },
          ]
        : []),
    ];

    const activeSort = sortModel?.[0];

    return {
      sortingColumn: activeSort?.field ?? 'ReviewJobID',
      direction: activeSort?.sort === 'asc' ? 'ASC' : 'DESC',
      displayLength: pageSize,
      pageNumber: pageNumber || 1,
      ...(columnFilter.length > 0 ? {columnFilter} : {}),
    };
  }, [filterModel, sortModel, pageNumber, pageSize, selectedGroupId]);

  useEffect(() => {
    onExportPayloadChange?.(exportPayload);
  }, [exportPayload, onExportPayloadChange]);

  useEffect(() => {
    setLiveJobsData(jobsData ?? null);
  }, [jobsData]);

  useEffect(() => {
    if (lastEvent?.payload?.type !== MANAGE_AI_REVIEW_JOBS_DATA_UPDATED) return;

    const payload = lastEvent?.payload?.payload;
    if (!payload?.jobId) return;

    const normalizedStatus = normalizeSignalRJobStatus(payload.jobStatus);
    const isTerminalStatus =
      normalizedStatus === AIReviewJobStatus.Complete ||
      normalizedStatus === AIReviewJobStatus.Failed;
    const eventJobId = Number(payload.jobId);
    const currentJobs = liveJobsData ?? jobsData;
    const currentJob = (currentJobs?.jobs ?? []).find(
      (job) => Number(job.jobId) === eventJobId
    );
    const isJobOnCurrentPage = Boolean(currentJob);
    const isStatusChangedToInProgress =
      normalizedStatus === AIReviewJobStatus.InProgress &&
      normalizeSignalRJobStatus(currentJob?.status) !==
        AIReviewJobStatus.InProgress;

    setLiveJobsData((previousData) => {
      const sourceData = previousData ?? jobsData ?? null;
      if (!sourceData?.jobs?.length) return previousData;

      let matched = false;
      const updatedJobs = sourceData.jobs.map((job) => {
        if (Number(job.jobId) !== eventJobId) {
          return job;
        }

        matched = true;

        const nextExecutionDate = getSignalRExecutionDate(payload);

        return {
          ...job,
          totalDocCount: payload.totalDocs ?? job.totalDocCount,
          completedDocCount: payload.completedDocs ?? job.completedDocCount,
          excludedErrorDocCount:
            payload.excludedOrErroredDocs ?? job.excludedErrorDocCount,
          status: normalizedStatus ?? job.status,
          executionDate:
            isTerminalStatus && nextExecutionDate
              ? nextExecutionDate
              : job.executionDate,
        };
      });

      if (!matched) {
        return sourceData;
      }

      return {
        ...sourceData,
        jobs: updatedJobs,
      };
    });

    if (!isJobOnCurrentPage) {
      return;
    }

    const shouldRefetchList =
      isTerminalStatus ||
      isStatusChangedToInProgress ||
      payload.excludedOrErroredDocs == null;

    if (!shouldRefetchList) {
      return;
    }

    if (lastRefetchedSignalREventRef.current === lastEvent) {
      return;
    }

    lastRefetchedSignalREventRef.current = lastEvent;
    fetchJobList();
  }, [jobsData, lastEvent]);

  const resolvedJobsData = liveJobsData ?? jobsData;

  const errorToNavigate = () => {
    setIsNavigatingToDocList(false);
    openJobActionToast(
      t('ai.review.jobList.jobListActions.errorNavigateToDocView'),
      ToastType.Error
    );
    return;
  };

  useEffect(() => {
    if (isGetMasterIdError) {
      errorToNavigate();
      return;
    }
    if (!isNavigatingToDocList) return;

    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListData?.doclistMasterId) &&
        navType === SourceTypeCode.DOCLIST
      ) {
        const backPath = `${window.location.pathname}${window.location.search}`;
        if (!backPath.toLowerCase().includes('ai/review')) return; // to avoid setting doc list back navigation
        dispatch(setDocListId(Number(docListData?.doclistMasterId)));
        dispatch(
          setDocListBackNavigation({
            label: t('ai.review.header.title'),
            path: backPath,
          })
        );
        setIsNavigatingToDocList(false);
        navigate('/Document/DocList');
      }
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      errorToNavigate();
      return;
    }
  }, [lastEvent, isGetMasterIdError]);

  const handleJobAndCountClicked = async (param: any, column: IColumnsData) => {
    if (docTypesMapping[column.field as docType]) {
      setIsNavigatingToDocList(true);
      setShowDocLimitWarning(true);
      goToDocListPayloadDetails.current = {
        reviewJobId: param?.row?.jobId || null,
        type: docTypesMapping[column.field as docType],
        totalDocsToView: param?.row?.[column.field as docType] ?? 0,
      };
      return;
    }
    navigate(
      `/AI/Review/JobResults?jobId=${param.row.jobId}&jobType=${param.row.jobType}&tab=jobDetails`
    );
  };

  const getColumns = (): GridColDef[] => {
    const columns: GridColDef[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 48,
        minWidth: 48,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          const rowStatus = parseJobListRowStatus(params.row?.status);
          const rowGroupName = (params.row?.jobGroupName ?? null) as
            | string
            | null;
          const rowJobName = (params.row?.jobName ?? null) as string | null;
          const rowJobId = params?.row?.jobId;
          return (
            <ArrIconButton
              onClick={(e) =>
                handleMoreIconClick(
                  e,
                  rowStatus,
                  rowGroupName,
                  rowJobName,
                  rowJobId
                )
              }
              icon={<MoreIcon aria-hidden="true" />}
              ariaLabel={t('ai.review.jobList.jobListActions.openFor', {
                jobName: rowJobName ?? rowJobId,
              })}
              shape="none"
              testId={`user-more-action-btn-${params.id}`}
              className="job-list-icon-button"
            />
          );
        },
      },
    ];

    columnsData.forEach((col: IColumnsData) => {
      columns.push({
        field: col.field,
        headerName: col.headerName,
        filterable: col.filterable,
        sortable: col.sortable,
        width: col.width,
        minWidth: col.width,
        align: (col?.align as GridAlignment) ?? 'left',
        renderHeader: (params) => {
          if (
            params.field === 'excludedErrorDocCount' ||
            params.colDef.field === 'excludedErrorDocCount'
          ) {
            return (
              <div className="excluded-error-header">
                <Typography
                  className="typo-text label2_size"
                  data-testid={`job-list-typography-excluded-error`}
                >
                  {t('ai.review.jobList.jobListColumns.excludedError')}
                </Typography>

                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      className="info-icon-label-joblist"
                      fontSize="medium"
                      role="button"
                      aria-label={t(
                        'ai.review.jobList.jobListColumns.excludedErrorInfoLabel'
                      )}
                      data-testid="excluded-error-info-tooltip-trigger"
                      tabIndex={0}
                    />
                  }
                >
                  <Trans
                    i18nKey="ai.review.jobList.jobListColumns.excludedErrorTooltip"
                    components={{
                      br: (
                        <br data-testid={`excluded-error-tooltip-content-br`} />
                      ),
                      strong: (
                        <strong
                          data-testid={`excluded-error-tooltip-content-strong`}
                        />
                      ),
                      p: <p data-testid={`excluded-error-tooltip-content-p`} />,
                      ul: (
                        <ul data-testid={`excluded-error-tooltip-content-ul`} />
                      ),
                      li: (
                        <li data-testid={`excluded-error-tooltip-content-li`} />
                      ),
                    }}
                  />
                </ArrTooltipWrapper>
              </div>
            );
          }

          return (
            <Typography
              className="typo-text label2_size"
              tabIndex={params.field === 'ReviewJobID' ? 0 : undefined}
              data-testid={`job-list-typography-text`}
            >
              {params.colDef.headerName}
            </Typography>
          );
        },
        renderCell: (param: any) => {
          const rawValue = param.row?.[col.fieldDataKey];
          const formattedText = formatCellValue(
            rawValue,
            param.row?.status,
            col
          );
          const normalizedStatus = String(
            param.row?.status ?? ''
          ).toLowerCase() as AIReviewJobStatus;
          const colorClass =
            formattedText === EMPTY_CELL_VALUE || rawValue === 0
              ? 'text-body-subtle'
              : (col.textColorClass[normalizedStatus] ?? 'text-headings');
          return (
            <>
              {col?.clickable &&
              !!col?.clickable[normalizedStatus] &&
              !!rawValue ? (
                <ArrButton
                  onClick={() => {
                    handleJobAndCountClicked(param, col);
                  }}
                  variant="text"
                  size="xs"
                  className="id-cell-preview"
                  dataTestId={`job-list-button-${param.id}`}
                  aria-label={
                    docTypesMapping[col.field]
                      ? t('ai.review.jobList.jobListActions.viewDocuments', {
                          count: param.row[col.fieldDataKey],
                          jobName: param.row.jobName ?? param.row.jobId,
                        })
                      : t('ai.review.jobList.jobListActions.openJob', {
                          jobName: param.row.jobName ?? param.row.jobId,
                        })
                  }
                >
                  {cellText(
                    formattedText,
                    colorClass,
                    `button-${col.headerName}-${param.id}`,
                    param,
                    col
                  )}
                </ArrButton>
              ) : (
                cellText(
                  formattedText,
                  colorClass,
                  `text-${col.headerName}-${param.id}`,
                  param,
                  col
                )
              )}
            </>
          );
        },
        renderHeaderFilter: (params) => {
          return (
            <>
              {col.filterable && showHeaderFilters && (
                <FilterTextField
                  dataTestId={col.headerName}
                  gridRenderHeaderFilterProps={params}
                  placeholder={t('common.filter')}
                  className="custom-filter"
                  aria-label={t('common.filterColumn', {
                    column: col.headerName,
                  })}
                />
              )}
            </>
          );
        },
      });
    });

    return columns;
  };

  useEffect(() => {
    const selectedGroupChanged =
      previousSelectedGroupIdRef.current !== selectedGroupId;
    previousSelectedGroupIdRef.current = selectedGroupId;

    const hasDefaultSort =
      sortModel?.length === 1 &&
      sortModel[0]?.field === 'ReviewJobID' &&
      sortModel[0]?.sort === 'desc';
    const shouldResetForGroup =
      selectedGroupChanged &&
      selectedGroupId !== null &&
      (pageNumber !== 1 ||
        pageSize !== tableProps.DEFAULT_PAGE_SIZE ||
        !hasDefaultSort ||
        filterModel.items.length > 0);

    if (shouldResetForGroup) {
      setPageNumber(1);
      setPageSize(tableProps.DEFAULT_PAGE_SIZE);
      setSortModel([{field: 'ReviewJobID', sort: 'desc'}]);
      setFilterModel({
        items: [],
      });
      return;
    }

    if (pageNumber < 1) {
      setPageNumber(1);
      return;
    }
    if (!sortModel || (selectedGroupChanged && sortModel.length === 0)) {
      setSortModel([{field: 'ReviewJobID', sort: 'desc'}]);
      return;
    }
    if (sortModel.length === 0) {
      return;
    }
    fetchJobList();
  }, [pageNumber, pageSize, sortModel, filterModel, selectedGroupId]);

  function fetchJobList() {
    const isUnfilteredAllJobsRequest =
      selectedGroupId === null && filterModel.items.length === 0;

    getJobList(
      {
        sortingColumn: sortModel?.[0]?.field ?? 'ReviewJobID',
        direction: sortModel?.[0]?.sort ?? 'desc',
        displayLength: pageSize, // keep -1 for all the rows
        pageNumber: pageNumber,
        columnFilter: [
          ...filterModel.items.map((item) => ({
            columnName: item.field,
            value: item.value,
          })),
          ...(selectedGroupId !== null
            ? [
                {
                  columnName: 'JobGroupId',
                  value: selectedGroupId.toString(),
                },
              ]
            : []),
        ],
      },
      {
        onSuccess: (response) => {
          if (!isUnfilteredAllJobsRequest) return;
          const total = getServerJobCount(response);
          if (total !== undefined) {
            onAllJobsCountChange?.(total);
          }
        },
      }
    );
  }

  useEffect(() => {
    if (previousJobGroupNamesSignatureRef.current === null) {
      previousJobGroupNamesSignatureRef.current = jobGroupNamesSignature;
      return;
    }
    if (previousJobGroupNamesSignatureRef.current === jobGroupNamesSignature) {
      return;
    }
    previousJobGroupNamesSignatureRef.current = jobGroupNamesSignature;
    fetchJobList();
  }, [jobGroupNamesSignature]);

  const handleRetryLoadJobs = () => {
    setHasJobListLoadError(false);
    fetchJobList();
  };

  const hasActiveFilters = filterModel.items.some(
    (item) => String(item.value ?? '').trim().length > 0
  );
  const apiJobs = hasJobListLoadError ? [] : (resolvedJobsData?.jobs ?? []);
  const shouldUseMockJobs =
    !hasJobListLoadError &&
    !hasActiveFilters &&
    !isLoadingJobs &&
    apiJobs.length === 0;
  const jobRows = apiJobs;
  const totalJobCount = getServerJobCount(resolvedJobsData);
  const showHeaderFilters =
    !hasJobListLoadError && (jobRows.length > 0 || hasActiveFilters);

  useEffect(() => {
    onHasJobsChange?.(jobRows.length > 0);
  }, [
    onHasJobsChange,
    resolvedJobsData,
    hasJobListLoadError,
    hasActiveFilters,
    isLoadingJobs,
  ]);

  const renderFilteredNoJobsOverlay = () => (
    <Box className="no-grid-overlay">
      <NoDataIcon aria-hidden="true" />
      <Typography
        className="typo-text headline3_size"
        data-testid="job-list-no-data"
      >
        {t('ai.review.jobList.messageNoData')}
      </Typography>
    </Box>
  );

  const renderJobListErrorOverlay = () => (
    <Box
      className="job-list-error-overlay"
      data-testid="job-list-error-overlay"
    >
      <Typography
        className="typo-text subhead1_size text-headings job-list-error-title"
        data-testid="job-list-error-title"
      >
        {t('ai.review.jobList.jobApiFailureTitle')}
      </Typography>
      <Typography
        className="typo-text body2_size text-body-subtle job-list-error-description"
        data-testid="job-list-error-description"
      >
        {t('ai.review.jobList.jobApiFailureDescription')}
      </Typography>
      <ArrButton
        variant="primary"
        size="small"
        dataTestId="job-list-error-refresh-button"
        onClick={handleRetryLoadJobs}
      >
        {t('common.refresh')}
      </ArrButton>
    </Box>
  );

  return (
    <>
      <ArrDocMaxLimitWarning
        totalDocumentCount={goToDocListPayloadDetails.current.totalDocsToView}
        show={showDocLimitWarning}
        destination={SourceTypeCode.DOCLIST}
        onContinue={() =>
          setGoToDocListPayload({
            reviewJobId: goToDocListPayloadDetails.current.reviewJobId,
            type: goToDocListPayloadDetails.current.type,
          })
        }
        onCancel={() => {
          setShowDocLimitWarning(false);
          setIsNavigatingToDocList(false);
        }}
      />
      <Box className="job-list">
        <ArrDataGridPro
          rowHeight={tableProps.ROW_HEIGHT}
          data-testid="ai-job-list-data-grid"
          columns={getColumns()}
          columnHeaderHeight={48}
          rows={jobRows}
          rowCount={
            hasJobListLoadError
              ? 0
              : shouldUseMockJobs
                ? jobRows.length
                : totalJobCount
          }
          noRowsOverlay={
            hasJobListLoadError
              ? () => renderJobListErrorOverlay()
              : hasActiveFilters
                ? () => renderFilteredNoJobsOverlay()
                : jobRows.length === 0
                  ? () => <NoJobs addJobs={() => props.setIsCreateJob(true)} />
                  : undefined
          }
          getRowId={(row: any) => row.jobId}
          showPerPageOptions
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          currentPage={pageNumber}
          onPageChange={setPageNumber}
          pageSizeOptions={tableProps.PAGE_SIZE_OPTIONS}
          pagination
          disableColumnResize={false}
          sortModel={sortModel ?? []}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          headerFilters={showHeaderFilters}
          headerFilterHeight={tableProps.FILTER_HEADER_HEIGHT}
          disableColumnReorder
          showCellVerticalBorder
          disableVirtualization
          entityType={t('common.entries')}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          disableColumnFilter
          loading={isLoadingJobs}
          loadingOverlay={() => <ArrLoadingSpinner />}
          alwaysShowPagination
          hideFooter={jobRows.length === 0 && !hasActiveFilters}
        />
        <ArrModal
          descriptionHasHtml={true}
          showModal={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          showClose={false}
          title={t('ai.review.jobList.deleteJobModal.title')}
          ariaLabel={t('ai.review.jobList.deleteJobModal.title')}
          description={t('ai.review.jobList.deleteJobModal.description', {
            jobName: pendingDeleteJobName ?? '',
          })}
          ariaDescription={t('ai.review.jobList.deleteJobModal.description', {
            jobName: pendingDeleteJobName ?? '',
          })}
          dataTestId="job-list-modal"
          buttons={[
            {
              id: 'cancel-delete-job',
              label: t('ai.review.jobList.deleteJobModal.cancelBtn'),
              onClick: handleCloseDeleteModal,
              variant: 'secondary',
              dataTestId: 'cancel-delete-job-button',
            } as ModalButton,
            {
              id: 'confirm-delete-job',
              label: t('ai.review.jobList.deleteJobModal.deleteBtn'),
              onClick: handleConfirmDeleteJob,
              variant: 'danger',
              dataTestId: 'confirm-delete-job-button',
            } as ModalButton,
          ]}
        />
        <ArrMenu
          id="action-menu"
          ref={menuItemsRef}
          items={
            hasActionsMenu
              ? menuItems
              : [{id: 'noop', label: '', value: 'noop'}]
          }
          onItemClick={() => {}}
          onClose={handleActionsMenuClose}
          direction="right"
          className="job-list__actions-menu"
          keepOpenOnItemClick={true}
          autoFocusMenu={true}
          disableAutoFocusItem={false}
          renderItem={(item, helpers) => {
            return (
              <JobListActionMenuItem
                item={item}
                helpers={helpers}
                selectedRowStatus={effectiveRowStatus}
                selectedRowGroupName={effectiveGroupName}
                selectedMenuGroupName={selectedMenuGroupName}
                selectedRowJobName={effectiveJobName}
                selectedRowJobId={effectiveJobId}
                isAddToGroupExpanded={isAddToGroupExpanded}
                groupSearchValue={groupSearchValue}
                groupSearchPerformed={groupSearchPerformed}
                visibleGroups={visibleGroups}
                onToggleAddToGroup={() =>
                  setIsAddToGroupExpanded((prev) => !prev)
                }
                onGroupSearchChange={handleGroupSearchChange}
                onGroupSearchKeyDown={handleGroupSearchKeyDown}
                onGroupSearchClear={handleGroupSearchClear}
                onAddToGroup={handleAddJobToGroup}
                onRemoveFromGroup={handleRemoveJobFromGroup}
                onOpenDeleteJob={(jobName, jobId) => {
                  setPendingDeleteJobName(jobName);
                  setPendingDeleteJobId(jobId);
                  setIsDeleteModalOpen(true);
                }}
                onRetryJob={handleRetryJob}
                onExportJobDetailsAsPdf={handleExportJobDetailsAsPdf}
              />
            );
          }}
        />
        {jobActionToast.isOpen && (
          <ArrToastNotification
            message={jobActionToast.message}
            status={[jobActionToast.status]}
            onClose={() => setJobActionToast(defaultJobActionToastState)}
          />
        )}

        {isNavigatingToDocList && (
          <ArrLoadingSpinner overlay isOpen={isNavigatingToDocList} />
        )}
      </Box>
    </>
  );
};

export default JobList;
